/* eslint-disabled no-unused-vars */

import { FC, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Form, Input, message, Skeleton } from 'antd';
import Box from 'src/components/Box';
import RichTextEditor from 'src/components/RichTextEditor';
import { UploadFileApi, UserResponse } from 'src/types';
import apiRequests from 'src/utils/api';
import apiRoutes from 'src/utils/apiRoutes';
import asyncErrorHandler from 'src/utils/asyncErrorHandler';
import { RootState } from 'src/store';
import { updateAuthData } from 'src/store/auth/reducer';
import './index.style.scss';

import SearchAutocomplete from '../../components/SearchAutocomplete';
import { getCurrentCustomer } from 'src/utils/localStorage';

const CompanyProfile: FC = () => {
  const user: UserResponse = useSelector(
    (globalState: any) => globalState.auth.user
  );
  const dispatch = useDispatch();

  const [form] = Form.useForm();

  const [noteImages] = useState<UploadFileApi[]>([]);
  const [submitLoading, setSubmitLoading] = useState(false);

  const projectRetainer = useSelector(
    (globalState: RootState) => globalState.projectRetainer
  );

  const customer = getCurrentCustomer();

  const formInitialState = {
    business_name: customer.business_name,
    billing_email: customer.billing_email,
    phone: customer.phone,
    website: customer.website,
    address_line1: customer.address_line1,
    city: customer.city,
    state: customer.state,
    zip: customer.zip,
    notes: customer.notes_delta ?? customer.notes,
  };

  const submitHandler = async (values: any) => {
    setSubmitLoading(true);

    try {
      const { billing_email, ...customerValues } = values;
      const currentCustomer = getCurrentCustomer();

      customerValues.uploads = customerValues.notes
        ? noteImages
            .filter(
              (x) => x.url && customerValues.notes.includes(`src="${x.url}"`)
            )
            .map((file) => file.response?.uuid)
        : [];

      const customerResponse = await apiRequests.put(
        `${apiRoutes.CUSTOMERS}/${customer.uuid}`,
        customerValues
      );

      dispatch(
        updateAuthData({
          ...user,
          contact: {
            ...user.contact,
            customer: {
              ...currentCustomer,
              ...customerResponse.data.data,
            },
          },
        })
      );

      message.success('Company profile updated.');
    } catch (error) {
      asyncErrorHandler(error);
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <Box>
      {projectRetainer.loading && !projectRetainer.data ? (
        <Skeleton />
      ) : (
        <div className="flex flex-wrap gap-6">
          <div className="w-full flex-none lg:flex-1">
            <Form
              className="default-form form-label-no-bold pt-2"
              form={form}
              layout="vertical"
              initialValues={formInitialState}
              onFinish={submitHandler}
            >
              <div className="mb-4 flex justify-between gap-2">
                <h2 className="text-2xl font-bold leading-none text-blue-700">
                  My account
                </h2>

                <Button
                  htmlType="submit"
                  type="primary"
                  loading={submitLoading}
                >
                  Save changes
                </Button>
              </div>

              <div className="flex flex-wrap gap-6">
                <div className="flex-1">
                  <Form.Item label="Company" name="business_name">
                    <Input disabled />
                  </Form.Item>

                  <div className="flex flex-wrap gap-x-2">
                    <Form.Item
                      label="Email"
                      name="billing_email"
                      className="flex-auto"
                    >
                      <Input disabled />
                    </Form.Item>

                    <Form.Item label="Phone" name="phone" className="flex-auto">
                      <Input />
                    </Form.Item>
                  </div>

                  <Form.Item label="Website" name="website">
                    <Input />
                  </Form.Item>

                  <Form.Item label="Address" name="address_line1">
                    <SearchAutocomplete
                      size="large"
                      onSelect={(address) => {
                        form.setFieldsValue({
                          address_line1: address.addressLabel,
                          city: address.city,
                          state: address.state,
                          zip: address.postalCode ?? null,
                        });
                      }}
                    />
                  </Form.Item>

                  <div className="flex flex-wrap gap-x-2">
                    <Form.Item
                      label="City"
                      name="city"
                      className="flex-auto sm:flex-1"
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      label="State"
                      name="state"
                      className="flex-auto sm:flex-1"
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      label="Zip code"
                      name="zip"
                      className="flex-auto sm:flex-1"
                    >
                      <Input />
                    </Form.Item>
                  </div>
                </div>

                {projectRetainer.data ? (
                  <div className="w-full lg:w-[520px] xl:w-full 2xl:w-[520px]">
                    <div className="ant-form-item-label ant-col">
                      <label>Notes</label>
                    </div>

                    <Form.Item name="notes" noStyle>
                      <RichTextEditor
                        className="min-h quill-scroll-small mb-6"
                        includeToolbar
                        imageAsData64={true}
                        onChange={(html, delta, source, editor) => {
                          const deltaJson = JSON.stringify(
                            editor.getContents()
                          );
                          form.setFieldsValue({
                            notes: html,
                            notes_delta: deltaJson,
                          });
                        }}
                      />
                    </Form.Item>

                    <Form.Item name="notes_delta" noStyle>
                      <Input type="hidden" />
                    </Form.Item>
                  </div>
                ) : undefined}
              </div>
            </Form>
          </div>
        </div>
      )}
    </Box>
  );
};

export default CompanyProfile;
